<script lang="ts">
  import Avatar from "$components/+blocks/Avatar/Avatar.svelte";
  import Separator from "$components/+blocks/Separator/Separator.svelte";
  import Badge from "$components/+blocks/Badge/Badge.svelte";
  import {
    selectRecentActivity,
    memberProfilePanel,
    isSearchFetched,
    selectForumsActiveIn,
  } from "$stores/memberPanel";
  import { parseDateTime } from "$utils/datetimes";
  import LoadingAtom from "$components/+blocks/Loading/LoadingAtom.svelte";
  import SocialIconLink from "$components/+blocks/SocialIconLink/SocialIconLink.svelte";
  import RecentActivity from "../RecentActivity.svelte";
  import OVIcon from "$components/+blocks/OVIcon/OVIcon.svelte";
  import { communitySlug } from "$stores/community";
  import { fade } from "svelte/transition";
  import { spaceURL } from "$utils/urls";
  import { isCommunityMember } from "$stores/permissions";
  import SidePanelInnerLayout from "$components/+layout/SidePanel/SidePanelInnerLayout.svelte";
  import { slugify } from "$utils/strings";
  import { Twitter, Github, LinkedIn, World, Squircle } from "$components/+blocks/OVIcon";
  import { isDarkTheme } from "app/stores/userPreferences";

  $: date =
    $memberProfilePanel.joinedCommunityAt &&
    parseDateTime($memberProfilePanel.joinedCommunityAt, "datetime is invalid")
      .setLocale("en-US")
      .toLocaleString();
</script>

<SidePanelInnerLayout>
  <span
    slot="title"
    class="col-start-2 col-end-3 mx-auto text-sm font-medium text-neutral-500 dark:text-neutral-100"
    >Member
  </span>

  <svelte:fragment slot="content">
    <div class="flex flex-col">
      <div class="flex flex-col items-center px-4 pt-4">
        <Avatar
          size="size-[58px]"
          mainImage={$memberProfilePanel.profileImage}
          label={$memberProfilePanel.handle[0]}
          backgroundColor={$memberProfilePanel.profileColor}
          radius={"rounded-lg"}
        />
        <span class="text-md mt-3 font-semibold text-neutral-600 dark:text-neutral-100"
          >{$memberProfilePanel.handle}</span
        >
        {#if $memberProfilePanel.headline}
          <span class="mt-3 text-xs font-medium dark:text-neutral-400"
            >{$memberProfilePanel.headline}</span
          >
        {/if}
        {#if $memberProfilePanel.bio}
          <span
            class="mt-3 text-center text-sm font-medium text-neutral-500/90 dark:text-neutral-300"
            >{$memberProfilePanel.bio}</span
          >
        {/if}

        <div class="mt-3 flex items-center gap-2">
          {#if $memberProfilePanel.twitter}
            <!-- Twitter is a handle -->
            <SocialIconLink
              href={`https://twitter.com/${$memberProfilePanel.twitter}`}
              icon={Twitter}
            />
          {/if}
          {#if $memberProfilePanel.github}
            <!-- Github is a username -->
            <SocialIconLink
              href={`https://github.com/${$memberProfilePanel.github}`}
              icon={Github}
            />
          {/if}
          {#if $memberProfilePanel.linkedin}
            <!-- LinkedIn is a full url -->
            <SocialIconLink href={$memberProfilePanel.linkedin} icon={LinkedIn} />
          {/if}
          {#if $memberProfilePanel.website}
            <SocialIconLink href={$memberProfilePanel.website} icon={World} />
          {/if}
        </div>
      </div>
    </div>

    <Separator class="mt-4" />
    <!-- hide unless user has permissions to fetch, otherwise it's empty -->
    {#if $isCommunityMember}
      <div class="scrollbar-thin overflow-x-hidden overflow-y-auto pt-4">
        <div class="flex w-full items-center gap-4 px-4 text-left text-xs font-medium">
          <div class="flex flex-col gap-2" in:fade|global>
            <span class="text-neutral-500/90 dark:text-neutral-400">Threads</span>
            {#if $isSearchFetched}
              <span class="text-neutral-500/90 dark:text-neutral-200"
                >{$memberProfilePanel.threadsCount}</span
              >
            {:else}
              <LoadingAtom size="h-4 w-[26px]" />
            {/if}
          </div>
          <div class="flex flex-col gap-2" in:fade|global>
            <span class="text-neutral-500/90 dark:text-neutral-400">Replies</span>
            {#if $isSearchFetched}
              <span class="text-neutral-500/90 dark:text-neutral-200"
                >{$memberProfilePanel.repliesCount}</span
              >
            {:else}
              <LoadingAtom size="h-4 w-[26px]" />
            {/if}
          </div>
          <div class="ml-auto flex flex-col gap-2">
            <span class="text-neutral-500/90 dark:text-neutral-400">Member since</span>
            <span class="text-neutral-500/90 dark:text-neutral-200">{date}</span>
          </div>
        </div>

        <div class="mt-4 mr-auto flex flex-col gap-2 px-4 text-left">
          <span class="text-xs font-medium text-neutral-500/90 dark:text-neutral-400">Roles</span>
          <div class="flex flex-wrap gap-2">
            {#if $memberProfilePanel.roles}
              {#each $memberProfilePanel.roles as role (role.uuid)}
                {#if !role.isAdmins && role.name}
                  <!-- use muted colours for workspace roles  -->
                  {@const communityRole = role.isTeam || role.isMembers}
                  <Badge
                    fontSize={"text-xxs"}
                    verticalPadding={"py-0.5"}
                    horizontalPadding={"px-1.5"}
                    lineHeight={"leading-none"}
                    backgroundColor={communityRole ? "bg-neutral-400 dark:bg-neutral-500" : ""}
                    customColor={!communityRole ? role.color || undefined : undefined}
                    radius={"rounded"}
                    >{role.name}
                  </Badge>
                {/if}
              {/each}
            {/if}
          </div>
        </div>

        {#if $selectForumsActiveIn.length > 0 && $isSearchFetched}
          <div class="mr-auto flex flex-col gap-1 px-4 text-left" in:fade|global={{ duration: 50 }}>
            <span class="mt-4 text-xs font-medium text-neutral-500/90 dark:text-neutral-400"
              >Forums active in</span
            >
            <div
              class="-ml-1 flex flex-wrap gap-1 text-xs font-medium text-neutral-500/90 transition-colors dark:text-neutral-200"
            >
              {#each $selectForumsActiveIn as forum (forum.uuid)}
                <a
                  href={$communitySlug &&
                    spaceURL({
                      communitySlug: $communitySlug,
                      spaceType: "forum",
                      spaceUuid: forum.uuid,
                      spaceSlug: slugify(forum.title),
                    })}
                  class="flex items-center gap-1 rounded-lg p-1 hover:bg-neutral-400/10 dark:hover:bg-white/5"
                >
                  {#if forum.icon}
                    {@const icon = $isDarkTheme && forum.iconDark ? forum.iconDark : forum.icon}
                    <Avatar mainImage={icon} radius="rounded-[3px]" />
                  {:else}
                    <span style="color: {forum.color}">
                      <OVIcon icon={Squircle} class="shrink-0" />
                    </span>
                  {/if}
                  {#if forum.title}
                    {forum.title}
                  {/if}
                </a>
              {/each}
            </div>
          </div>
        {/if}

        <Separator class="mt-4" />

        {#if $selectRecentActivity.length > 0 && $isSearchFetched}
          <div class="flex w-full flex-col" in:fade|global={{ duration: 50, delay: 80 }}>
            <span class="m-4 text-xs font-medium text-neutral-500/90 dark:text-neutral-400"
              >Recent activity</span
            >
            {#each $selectRecentActivity as activity (activity.created)}
              <RecentActivity {activity} />
            {/each}
          </div>
        {/if}
      </div>
    {/if}
  </svelte:fragment>
</SidePanelInnerLayout>
